import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import errorIcon from './images/assets/fail.svg';

const OrderSimulate = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(null);
    const [showErrorPopup, setShowErrorPopup] = useState(false); // New state for popup
    const THRESHOLD = 48;
    const fileInputRef = useRef(null);

    const handleUploadButtonClick = () => {
        fileInputRef.current.click();
    };

    const handleSearch = async (e) => {
        const term = e.target.value;
        setSearchTerm(term);

        if (term.length > 2) {
            setLoading(true);
            try {
                const response = await fetch(`/api/articleSearch?query=${encodeURIComponent(term)}`);
                if (!response.ok) throw new Error('Search failed');
                const data = await response.json();
                setSearchResults(data);
                setSearchError(null);
            } catch (err) {
                setSearchError(err.message);
            } finally {
                setLoading(false);
            }
        } else {
            setSearchResults([]);
        }
    };

    const handleSelectItem = (item) => {
        setSelectedItems([...selectedItems, { ...item, quantity: '', error: null }]);
        setSearchResults([]);
        setSearchTerm('');
    };

    const handleQuantityChange = (index, value) => {
        const newItems = [...selectedItems];
        const numericValue = value === '' ? '' : Math.max(0, parseInt(value) || 0);

        newItems[index].quantity = numericValue;
        newItems[index].error = null;

        if (numericValue !== '' && numericValue < newItems[index].cv_step) {
            //newItems[index].error = `Quantity must be ≥ ${newItems[index].cv_step}`;
        }

        if (numericValue !== '') {
            const cvSize = parseFloat(newItems[index].cv_size) || 1;
            const convertedQty = numericValue * cvSize;
            const equivalentCases = convertedQty / cvSize;
            if (equivalentCases % 1 !== 0) {
                newItems[index].error = `Quantity must be in denominations of ${newItems[index].cv_step}`;
            }
        }

        setSelectedItems(newItems);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        setUploadError(null);
        setUploadSuccess(null);
        setShowErrorPopup(false); // Reset popup

        if (!file) return;

        const validTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ];
        if (!validTypes.includes(file.type)) {
            setUploadError('Please upload a valid Excel file (.xls or .xlsx)');
            setShowErrorPopup(true);
            return;
        }

        try {
            setLoading(true);

            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { cellText: true, raw: true });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false });

            const headers = jsonData[0].map((h) => h.toString().toLowerCase());
            if (headers[0] !== 'lcbo #' || headers[1] !== 'quantity') {
                setUploadError('Excel file must have "LCBO #" and "Quantity" columns');
                setShowErrorPopup(true);
                return;
            }

            const items = jsonData.slice(1).map((row) => ({
                cspc: row[0] != null ? String(row[0]).trim() : '',
                quantity: row[1] != null ? String(row[1]).trim() : '',
            }));

            if (items.length === 0) {
                setUploadError('Excel file contains no data');
                setShowErrorPopup(true);
                return;
            }

            const cspcs = items.map((item) => item.cspc).filter((cspc) => cspc);
            if (cspcs.length === 0) {
                setUploadError('No valid CSPCs found in the file');
                setShowErrorPopup(true);
                return;
            }

            const response = await fetch('/api/validateCSPCs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cspcs }),
            });

            if (!response.ok) throw new Error('CSPC validation failed');
            const { articles, invalidCSPCs } = await response.json();

            const newItems = articles.map((article) => {
                const item = items.find((i) => i.cspc === article.cspc);
                const quantity = item?.quantity || '';
                let error = null;

                if (quantity !== '' && !isNaN(quantity)) {
                    const numericQty = Math.max(0, parseInt(quantity) || 0);
                    if (numericQty < article.cv_step) {
                        //error = `Quantity must be ≥ ${article.cv_step}`;
                    } else {
                        const cvSize = parseFloat(article.cv_size) || 1;
                        const convertedQty = numericQty * cvSize;
                        const equivalentCases = convertedQty / cvSize;
                        if (equivalentCases % 1 !== 0) {
                            error = `Quantity must be in denominations of ${article.cv_step}`;
                        }
                    }
                }

                return { ...article, quantity, error };
            });

            setSelectedItems([...selectedItems, ...newItems]);

            if (invalidCSPCs.length > 0) {
                setUploadError(`Invalid LCBO #s: ${invalidCSPCs.join(', ')}`);
                setShowErrorPopup(true);
            } else {
                setUploadSuccess('File uploaded successfully');
            }
        } catch (err) {
            setUploadError(err.message || 'Error processing file');
            setShowErrorPopup(true);
        } finally {
            setLoading(false);
            e.target.value = null;
        }
    };

    // Close popup
    const closeErrorPopup = () => {
        setShowErrorPopup(false);
        setUploadError(null);
    };

    const calculateConversions = (item) => {
        if (!item.quantity || item.quantity === '') {
            return { convertedQty: 0, equivalentCases: 0, equivalent24s: 0 };
        }

        const quantity = parseInt(item.quantity);
        const packSize = parseInt(item.pack_size) || 0;
        const cvSize = parseFloat(item.cv_size) || 1;

        const convertedQty = quantity * cvSize;
        const equivalentCases = convertedQty / cvSize;
        const equivalent24s = convertedQty / 24;

        return {
            convertedQty,
            equivalentCases: equivalentCases.toFixed(2),
            equivalent24s: equivalent24s.toFixed(2),
        };
    };

    const calculateTotalEquivalentCases = () => {
        return selectedItems
            .reduce((total, item) => {
                const { equivalentCases } = calculateConversions(item);
                return total + (parseFloat(equivalentCases) || 0);
            }, 0)
            .toFixed(2);
    };

    const calculateTotalEquivalent24s = () => {
        return selectedItems
            .reduce((total, item) => {
                const { equivalent24s } = calculateConversions(item);
                return total + (parseFloat(equivalent24s) || 0);
            }, 0)
            .toFixed(2);
    };

    const totalEquivalentCases = calculateTotalEquivalentCases();
    const totalEquivalent24s = calculateTotalEquivalent24s();
    const thresholdStatus = Math.abs((THRESHOLD - totalEquivalent24s).toFixed(2));

    return (
        <div className="page-content">
            <div className='moqHeader'>
                <h1>Minimum Order Quantity Calculator</h1>
            </div>
            <div className="article-search-container">
                <div className="search-section">
                    <div className="search-section1">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearch}
                            placeholder="Search by LCBO # or description"
                            className="search-input"
                        />
                        {loading && <p>Loading...</p>}
                        {searchError && <p className="error">{searchError}</p>}

                        {searchResults.length > 0 && (
                            <ul className="search-results">
                                {searchResults.map((item) => (
                                    <li
                                        key={item.tbs_article_id}
                                        onClick={() => handleSelectItem(item)}
                                        className="result-item"
                                    >
                                        {item.tbs_article_description}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className="search-section2">
                    </div>
                    <div className="search-section3">
                        <input
                            type="file"
                            accept=".xls,.xlsx"
                            onChange={handleFileUpload}
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                        />
                        <button
                            onClick={handleUploadButtonClick}
                            className="actionButton"
                        >
                            UPLOAD
                        </button>
                    </div>
                </div>

                <div className="selected-items-section">
                    <table className="items-table">
                        <thead>
                            <tr>
                                <th>LCBO #</th>
                                <th>Description</th>
                                <th className="center">Case Quantity</th>
                                <th className="center">Containers</th>
                                <th className="center">Equivalent 24s</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedItems.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="empty-table">
                                        Add products by searching or by uploading from{' '}
                                        <a
                                            href={`${process.env.PUBLIC_URL}/MinimumOrderQuantity.xlsx`}
                                            download
                                            className="template-link"
                                        >
                                            template
                                        </a>
                                    </td>
                                </tr>
                            ) : (
                                selectedItems.map((item, index) => {
                                    const { convertedQty, equivalentCases, equivalent24s } =
                                        calculateConversions(item);
                                    return (
                                        <>
                                            <tr key={`${item.tbs_article_id}-data`} className="data-row">
                                                <td>{item.cspc}</td>
                                                <td>{item.tbs_article_description}</td>
                                                <td>
                                                    <div className="centeredInput">
                                                        <input
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) =>
                                                                handleQuantityChange(index, e.target.value)
                                                            }
                                                            min={item.cv_step}
                                                            className="quantity-input center"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="center">{convertedQty}</td>
                                                <td className="center">{equivalent24s}</td>
                                            </tr>
                                            {item.error && (
                                                <tr
                                                    key={`${item.tbs_article_id}-error`}
                                                    className="error-row"
                                                >
                                                    <td colSpan="5">
                                                        <span className="row-error">{item.error}</span>
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    );
                                })
                            )}
                        </tbody>
                    </table>

                    <div className="threshold-summary">
                        <div className="threshold-category">
                            <div className="threshold-value">{totalEquivalent24s}</div>
                            <div>Equivalent 24s</div>
                        </div>
                        <div className="threshold-category">
                            <div className="threshold-value">{thresholdStatus}</div>
                            <div>
                                Equivalent 24s{' '}
                                {totalEquivalent24s >= THRESHOLD ? 'above' : 'below'} threshold
                            </div>
                        </div>
                        <div className="threshold-category">
                            <div
                                className={`threshold-value ${totalEquivalent24s >= THRESHOLD ? 'positive' : 'negative'
                                    }`}
                            >
                                {totalEquivalent24s >= THRESHOLD ? 'YES' : 'NO'}
                            </div>
                            <div>Minimum Order Quantity for Delivery Met?</div>
                        </div>
                    </div>
                </div>

                {/* Error Popup */}
                {showErrorPopup && (
                    <div className="popup-container">
                        <div className="popup-overlay" />
                        <div className="popup-content">
                            <div className='popupParent'>
                                <div className='popupWarningIcon'>
                                    <img src={errorIcon} />
                                </div>
                                <div className='popupBody'>
                                    <div className='popupTexts'>
                                        <div className='popupMainText'>Error</div>
                                        <div className='popupSubText'>{uploadError}</div>
                                    </div>
                                </div>

                                <div className='buttonDiv'>
                                    <button
                                        onClick={closeErrorPopup}
                                        className="actionButton popConfirm"
                                    >
                                        CLOSE
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderSimulate;